'use client';

import { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion';
import Link from 'next/link';
import Image from 'next/image';
import {
  FaAnchor, FaShip, FaWater, FaGraduationCap, FaClipboardCheck,
  FaCalendarAlt, FaPhone, FaChevronDown, FaCheck,
  FaTimes, FaLifeRing, FaCompass, FaCertificate,
  FaUserGraduate, FaHandshake, FaGlobe, FaInfinity,
  FaClock, FaArrowRight, FaArrowLeft
} from 'react-icons/fa';
import { GiSpeedBoat } from 'react-icons/gi';
import { HiSparkles } from 'react-icons/hi2';
import { IoMdBoat } from 'react-icons/io';
import { courseSlug } from '@/lib/utils';

// Types for API data
interface MotorowodneCourse {
  id: number;
  tytul: string;
  kodimo: string | null;
  kodimoopis: string | null;
  level: string | null;
  motorowodne: number;
  obraz_kursu: string | null;
  cena: string | null;
  czastrwania: string | null;
  programszkolenia: string | null;
  categoryName: string;
  categoryShortName: string;
  categoryDescription: string;
  upcomingEvents: any[];
}

// CMS Content types
interface CMSContentItem {
  id: number;
  key: string;
  title: string | null;
  content: string | null;
  extra_data: any;
  display_order: number;
}

interface CMSContent {
  [group: string]: {
    [key: string]: {
      title: string | null;
      content: string | null;
      extra_data: any;
    };
  };
}

interface CMSFlat {
  [group: string]: CMSContentItem[];
}

// Type for comparison table row
interface ComparisonRow {
  feature: string;
  sm: string | boolean;
  msm: string | boolean;
}

// Icon mapping for dynamic content
const ICON_MAP: Record<string, React.ReactNode> = {
  water: <FaWater className="w-7 h-7" />,
  speedboat: <GiSpeedBoat className="w-7 h-7" />,
  lifering: <FaLifeRing className="w-7 h-7" />,
  compass: <FaCompass className="w-7 h-7" />,
  infinity: <FaInfinity className="w-7 h-7" />,
  globe: <FaGlobe className="w-7 h-7" />,
  handshake: <FaHandshake className="w-7 h-7" />,
  sparkles: <HiSparkles className="w-7 h-7" />,
  certificate: <FaCertificate className="w-7 h-7" />,
  clipboard: <FaClipboardCheck className="w-7 h-7" />,
  graduate: <FaUserGraduate className="w-7 h-7" />,
  graduation: <FaGraduationCap className="w-7 h-7" />,
  anchor: <FaAnchor className="w-7 h-7" />,
  ship: <FaShip className="w-7 h-7" />,
};

// Subtle scroll-animated wave for background decoration - with actual wave motion
const SubtleWave = ({
  scrollProgress,
  position = 'top',
  flip = false,
  color = 'rgba(100, 116, 139, 0.15)',
  amplitude = 20,
  frequency = 1,
  yOffset = 0
}: {
  scrollProgress: any;
  position?: 'top' | 'bottom';
  flip?: boolean;
  color?: string;
  amplitude?: number;
  frequency?: number;
  yOffset?: number;
}) => {
  // Calculate lighter colors for secondary and tertiary waves
  const getOpacity = (baseColor: string, multiplier: number): string => {
    const match = baseColor.match(/([\d.]+)\)$/);
    if (match) {
      const opacity = parseFloat(match[1]) * multiplier;
      return baseColor.replace(/[\d.]+\)$/, `${opacity.toFixed(2)})`);
    }
    return baseColor;
  };

  return (
    <div
      className={`absolute left-0 right-0 pointer-events-none overflow-hidden ${position === 'top' ? 'top-0' : 'bottom-0'}`}
      style={{
        height: 120,
        transform: flip ? 'scaleY(-1)' : 'none',
        marginTop: position === 'top' ? yOffset : 0,
        marginBottom: position === 'bottom' ? yOffset : 0,
        zIndex: 1,
      }}
    >
      {/* Wave 1 - Main wave with continuous flowing animation */}
      <motion.svg
        viewBox="0 0 1440 120"
        className="absolute w-[200%] h-full"
        preserveAspectRatio="none"
        animate={{
          x: [0, -720, 0],
        }}
        transition={{
          duration: 8 / frequency,
          ease: "easeInOut",
          repeat: Infinity,
          repeatType: "reverse",
        }}
      >
        <motion.path
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          animate={{
            d: [
              "M0,60 C120,30 240,90 360,60 C480,30 600,90 720,60 C840,30 960,90 1080,60 C1200,30 1320,90 1440,60 C1560,30 1680,90 1800,60 C1920,30 2040,90 2160,60 C2280,30 2400,90 2520,60 C2640,30 2760,90 2880,60",
              "M0,60 C120,90 240,30 360,60 C480,90 600,30 720,60 C840,90 960,30 1080,60 C1200,90 1320,30 1440,60 C1560,90 1680,30 1800,60 C1920,90 2040,30 2160,60 C2280,90 2400,30 2520,60 C2640,90 2760,30 2880,60",
            ],
          }}
          transition={{
            duration: 3 / frequency,
            ease: "easeInOut",
            repeat: Infinity,
            repeatType: "reverse",
          }}
        />
      </motion.svg>

      {/* Wave 2 - Secondary wave with offset timing */}
      <motion.svg
        viewBox="0 0 1440 120"
        className="absolute w-[200%] h-full"
        preserveAspectRatio="none"
        animate={{
          x: [-360, 0, -360],
        }}
        transition={{
          duration: 10 / frequency,
          ease: "easeInOut",
          repeat: Infinity,
          repeatType: "reverse",
        }}
      >
        <motion.path
          fill="none"
          stroke={getOpacity(color, 0.7)}
          strokeWidth="1.5"
          strokeLinecap="round"
          animate={{
            d: [
              "M0,70 C180,40 360,100 540,70 C720,40 900,100 1080,70 C1260,40 1440,100 1620,70 C1800,40 1980,100 2160,70 C2340,40 2520,100 2700,70 C2880,40 3060,100 3240,70",
              "M0,70 C180,100 360,40 540,70 C720,100 900,40 1080,70 C1260,100 1440,40 1620,70 C1800,100 1980,40 2160,70 C2340,100 2520,40 2700,70 C2880,100 3060,40 3240,70",
            ],
          }}
          transition={{
            duration: 4 / frequency,
            ease: "easeInOut",
            repeat: Infinity,
            repeatType: "reverse",
            delay: 0.5,
          }}
        />
      </motion.svg>

      {/* Wave 3 - Tertiary wave */}
      <motion.svg
        viewBox="0 0 1440 120"
        className="absolute w-[200%] h-full"
        preserveAspectRatio="none"
        animate={{
          x: [0, -480, 0],
        }}
        transition={{
          duration: 12 / frequency,
          ease: "easeInOut",
          repeat: Infinity,
          repeatType: "reverse",
        }}
      >
        <motion.path
          fill="none"
          stroke={getOpacity(color, 0.5)}
          strokeWidth="1"
          strokeLinecap="round"
          animate={{
            d: [
              "M0,50 C150,80 300,20 450,50 C600,80 750,20 900,50 C1050,80 1200,20 1350,50 C1500,80 1650,20 1800,50 C1950,80 2100,20 2250,50 C2400,80 2550,20 2700,50 C2850,80 3000,20 3150,50",
              "M0,50 C150,20 300,80 450,50 C600,20 750,80 900,50 C1050,20 1200,80 1350,50 C1500,20 1650,80 1800,50 C1950,20 2100,80 2250,50 C2400,20 2550,80 2700,50 C2850,20 3000,80 3150,50",
            ],
          }}
          transition={{
            duration: 5 / frequency,
            ease: "easeInOut",
            repeat: Infinity,
            repeatType: "reverse",
            delay: 1,
          }}
        />
      </motion.svg>
    </div>
  );
};

// Sticky in-page navigation — appears after hero, tracks active section
const NAV_LINKS = [
  { id: 'patenty', label: 'Patenty' },
  { id: 'kursy', label: 'Kursy' },
  { id: 'terminy', label: 'Najbliższe terminy' },
  { id: 'przebieg', label: 'Przebieg szkolenia' },
  { id: 'faq', label: 'FAQ' },
];

const StickyAnchorNav = () => {
  const [active, setActive] = useState<string>('');
  const [stuck, setStuck] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Detect when nav becomes "stuck" (top of viewport) — adds shadow when stuck
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const obs = new IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting),
      { threshold: 0 }
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, []);

  // Track which section is active in viewport
  useEffect(() => {
    const sections = NAV_LINKS.map((l) => document.getElementById(l.id)).filter(Boolean) as HTMLElement[];
    if (sections.length === 0) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <div ref={sentinelRef} aria-hidden className="h-0" />
      <nav
        className={`sticky top-0 z-30 backdrop-blur-md transition-shadow duration-300 ${
          stuck ? 'bg-white/85 shadow-md border-b border-slate-200/60' : 'bg-white/60 border-b border-slate-200/40'
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between gap-4 h-14">
          <div className="flex items-center gap-1.5 text-[rgb(6,40,95)] font-semibold text-sm shrink-0">
            <GiSpeedBoat className="w-5 h-5" />
            <span className="hidden sm:inline">Motorowodne</span>
          </div>
          <ul className="flex items-center gap-1 md:gap-2 overflow-x-auto scrollbar-none -mx-2 px-2">
            {NAV_LINKS.map((link) => {
              const isActive = active === link.id;
              return (
                <li key={link.id}>
                  <a
                    href={`#${link.id}`}
                    className={`relative whitespace-nowrap px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                      isActive
                        ? 'text-[rgb(6,40,95)] bg-sky-50'
                        : 'text-slate-600 hover:text-[rgb(6,40,95)] hover:bg-slate-50'
                    }`}
                  >
                    {link.label}
                    {isActive && (
                      <motion.span
                        layoutId="active-nav-pill"
                        className="absolute inset-0 rounded-full ring-1 ring-sky-200/80"
                        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                      />
                    )}
                  </a>
                </li>
              );
            })}
          </ul>
          <Link
            href="/contact"
            className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 font-semibold rounded-full text-sm hover:shadow-lg hover:shadow-amber-500/30 transition-shadow shrink-0"
          >
            <FaPhone className="w-3.5 h-3.5" />
            Zapisz się
          </Link>
        </div>
      </nav>
    </>
  );
};

// Counter Animation Component
const AnimatedCounter = ({ end, duration = 2, suffix = '' }: { end: number; duration?: number; suffix?: string }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let start = 0;
      const increment = end / (duration * 60);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 1000 / 60);
      return () => clearInterval(timer);
    }
  }, [isInView, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

// Enhanced FAQ Accordion Item with glass effect
const FAQItem = ({ question, answer, isOpen, onClick, index }: { question: string; answer: string; isOpen: boolean; onClick: () => void; index: number }) => (
  <motion.div
    className="overflow-hidden"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.05 }}
  >
    <button
      onClick={onClick}
      className={`w-full py-5 px-6 flex items-center justify-between text-left group transition-all duration-300 rounded-2xl mb-2 ${
        isOpen
          ? 'bg-gradient-to-r from-[rgb(6,40,95)] to-[rgb(15,60,130)] text-white shadow-lg shadow-blue-900/20'
          : 'bg-white/60 backdrop-blur-sm hover:bg-white/80 border border-slate-200/50'
      }`}
    >
      <span className={`text-base font-semibold pr-4 transition-colors ${isOpen ? 'text-white' : 'text-slate-700'}`}>
        {question}
      </span>
      <motion.div
        animate={{ rotate: isOpen ? 180 : 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="flex-shrink-0"
      >
        <FaChevronDown className={`w-4 h-4 ${isOpen ? 'text-amber-300' : 'text-slate-400'}`} />
      </motion.div>
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          <div className="px-6 pb-4 pt-2">
            <p className="text-slate-600 leading-relaxed">{answer}</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

// Dynamic Course Card from Database
// Per-level styling — kept as static map so Tailwind doesn't purge dynamic classes
const COURSE_STYLE: Record<number, { badge: string; glow: string; hoverTitle: string }> = {
  1: {
    badge: 'bg-[#0D59D3]',
    glow: 'hover:shadow-[0px_0px_50px_-5px_rgba(13,89,211,0.55)]',
    hoverTitle: 'group-hover:text-[#0D59D3]',
  },
  2: {
    badge: 'bg-indigo-700',
    glow: 'hover:shadow-[0px_0px_50px_-5px_rgba(67,56,202,0.55)]',
    hoverTitle: 'group-hover:text-indigo-700',
  },
  3: {
    badge: 'bg-emerald-700',
    glow: 'hover:shadow-[0px_0px_50px_-5px_rgba(5,150,105,0.55)]',
    hoverTitle: 'group-hover:text-emerald-700',
  },
  4: {
    badge: 'bg-amber-600',
    glow: 'hover:shadow-[0px_0px_50px_-5px_rgba(217,119,6,0.55)]',
    hoverTitle: 'group-hover:text-amber-700',
  },
};
const DEFAULT_COURSE_STYLE = {
  badge: 'bg-[rgb(6,40,95)]',
  glow: 'hover:shadow-[0px_0px_50px_-5px_rgba(6,40,95,0.55)]',
  hoverTitle: 'group-hover:text-[rgb(6,40,95)]',
};

const CourseCard = ({ course }: { course: MotorowodneCourse }) => {
  const imgSrc = course.obraz_kursu ? `/images/${course.obraz_kursu}` : null;
  const style = COURSE_STYLE[course.motorowodne] || DEFAULT_COURSE_STYLE;
  const upcomingCount = course.upcomingEvents?.length || 0;
  const nextDate = upcomingCount > 0 ? new Date(course.upcomingEvents[0].datarozpoczecia) : null;
  const programPreview = course.programszkolenia
    ? course.programszkolenia.replace(/\s+/g, ' ').trim()
    : '';

  return (
    <Link href={`/training/${courseSlug(course.id, course.tytul)}`} className="block h-full group">
      <div
        className={`flex flex-col h-full bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300 group-hover:-translate-y-2 ${style.glow}`}
      >
        {/* Image header — clean, with hover scale + dark overlay (matches katalog/kalendarz) */}
        <div className="relative h-48 w-full bg-gray-100 overflow-hidden">
          {imgSrc ? (
            <Image
              src={imgSrc}
              alt={course.tytul}
              fill
              loading="lazy"
              sizes="(max-width: 640px) 88vw, (max-width: 768px) 60vw, (max-width: 1024px) 50vw, 33vw"
              className="object-cover transition-transform duration-700 ease-in-out group-hover:scale-110"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-[rgb(6,40,95)] to-[rgb(13,89,211)] flex items-center justify-center text-white/30">
              <GiSpeedBoat className="w-16 h-16" />
            </div>
          )}
          {/* Hover darken overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          {/* Category badge top-right (per-level color) */}
          <div
            className={`absolute top-4 right-4 ${style.badge} text-white px-3 py-1 rounded-full text-sm font-medium transition-transform duration-300 group-hover:scale-105 group-hover:shadow-md`}
          >
            {course.categoryShortName || 'Motorowodne'}
          </div>
        </div>

        {/* Body */}
        <div className="p-6 flex flex-col flex-1 group-hover:bg-blue-50/30 transition-colors duration-300">
          {/* Title — fixed height for grid alignment */}
          <div className="h-16 mb-3">
            <h3 className={`text-xl font-bold text-gray-800 line-clamp-2 leading-snug transition-colors duration-300 ${style.hoverTitle}`}>
              {course.tytul}
            </h3>
          </div>

          {/* Short program preview */}
          {programPreview && (
            <p className="text-sm text-gray-600 line-clamp-2 mb-4">
              {programPreview}
            </p>
          )}

          {/* Meta info — duration + nearest term + count (calendar-style icons) */}
          <div className="space-y-2 mt-auto">
            {course.czastrwania && (
              <div className="flex items-center text-sm text-gray-600 transition-transform duration-300 group-hover:translate-x-1">
                <FaClock className="mr-2 text-[rgb(6,40,95)]" />
                <span>{course.czastrwania.split('\n')[0]}</span>
              </div>
            )}
            {nextDate && (
              <div className="flex items-center text-sm text-gray-600 transition-transform duration-300 group-hover:translate-x-1">
                <FaCalendarAlt className="mr-2 text-[rgb(6,40,95)]" />
                <span>
                  Najbliższy termin: <strong>{nextDate.getDate()} {PL_MONTHS_SHORT[nextDate.getMonth()]} {nextDate.getFullYear()}</strong>
                </span>
              </div>
            )}
            {upcomingCount > 1 && (
              <div className="flex items-center text-sm text-gray-500">
                <FaInfinity className="mr-2 text-[rgb(6,40,95)]/50" />
                <span>+{upcomingCount - 1} kolejnych terminów</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-4 pt-4 border-t border-gray-100 group-hover:border-blue-100 transition-colors duration-300 flex items-center justify-between">
            <span className="text-[rgb(6,40,95)] font-medium group-hover:underline flex items-center">
              Zobacz szczegóły
              <FaArrowRight className="ml-1 w-0 h-4 opacity-0 group-hover:w-4 group-hover:opacity-100 transition-all duration-300" />
            </span>
            {course.cena && (
              <span className="text-xs text-gray-500 truncate max-w-[55%] text-right">
                {course.cena.match(/\d[\d\s]*PLN/)?.[0] || 'Cena w opisie'}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

// Horizontal carousel for course cards — scroll-snap based (touch + arrows)
const CoursesCarousel = ({ courses }: { courses: MotorowodneCourse[] }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollPrev(el.scrollLeft > 8);
    setCanScrollNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
    // Active dot — based on first card whose left edge ≥ container scrollLeft
    const cards = Array.from(el.querySelectorAll<HTMLElement>('[data-card]'));
    if (cards.length === 0) return;
    const containerLeft = el.getBoundingClientRect().left;
    let nextActive = 0;
    for (let i = 0; i < cards.length; i++) {
      const cardLeft = cards[i].getBoundingClientRect().left;
      if (cardLeft - containerLeft <= 16) nextActive = i;
    }
    setActiveIndex(nextActive);
  };

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [courses.length]);

  const scrollByCard = (dir: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>('[data-card]');
    const step = card ? card.offsetWidth + 24 : 320; // 24 = gap-6
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  const scrollToCard = (i: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelectorAll<HTMLElement>('[data-card]')[i];
    if (card) el.scrollTo({ left: card.offsetLeft - 4, behavior: 'smooth' });
  };

  return (
    <div className="relative">
      {/* Arrows — desktop only (touch users swipe) */}
      <button
        onClick={() => scrollByCard(-1)}
        disabled={!canScrollPrev}
        aria-label="Poprzedni kurs"
        className={`hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -ml-5 z-10 w-11 h-11 items-center justify-center bg-white shadow-lg rounded-full border border-slate-100 transition-all ${
          canScrollPrev ? 'hover:bg-slate-50 hover:scale-105 cursor-pointer text-[rgb(6,40,95)]' : 'opacity-40 cursor-not-allowed text-slate-400'
        }`}
      >
        <FaArrowLeft className="w-4 h-4" aria-hidden="true" />
      </button>
      <button
        onClick={() => scrollByCard(1)}
        disabled={!canScrollNext}
        aria-label="Następny kurs"
        className={`hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 -mr-5 z-10 w-11 h-11 items-center justify-center bg-white shadow-lg rounded-full border border-slate-100 transition-all ${
          canScrollNext ? 'hover:bg-slate-50 hover:scale-105 cursor-pointer text-[rgb(6,40,95)]' : 'opacity-40 cursor-not-allowed text-slate-400'
        }`}
      >
        <FaArrowRight className="w-4 h-4" aria-hidden="true" />
      </button>

      {/* Scroll-snap track */}
      <div
        ref={scrollRef}
        className="flex overflow-x-auto snap-x snap-mandatory gap-6 -mx-4 px-4 pb-4 scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {courses.map((course) => (
          <div
            key={course.id}
            data-card
            className="snap-start shrink-0 w-[88%] sm:w-[60%] md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]"
          >
            <CourseCard course={course} />
          </div>
        ))}
      </div>

      {/* Dots indicator */}
      {courses.length > 1 && (
        <div className="flex justify-center mt-6 gap-1.5">
          {courses.map((_, i) => (
            <button
              key={i}
              onClick={() => scrollToCard(i)}
              aria-label={`Przejdź do kursu ${i + 1}`}
              className={`h-2 rounded-full transition-all ${
                i === activeIndex ? 'w-6 bg-[rgb(6,40,95)]' : 'w-2 bg-slate-300 hover:bg-slate-400'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Upcoming term flat row built from courses + their upcomingEvents
interface UpcomingTerm {
  eventId: number;
  courseId: number;
  courseTitle: string;
  categoryShortName: string;
  motorowodne: number;
  datarozpoczecia: string;
  datazakonczenia: string;
  cena: string | null;
}

const PL_MONTHS_SHORT = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'];

const formatDateRange = (start: string, end: string) => {
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime())) return start;
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  if (sameMonth) {
    return `${s.getDate()}–${e.getDate()} ${PL_MONTHS_SHORT[s.getMonth()]} ${s.getFullYear()}`;
  }
  return `${s.getDate()} ${PL_MONTHS_SHORT[s.getMonth()]} – ${e.getDate()} ${PL_MONTHS_SHORT[e.getMonth()]} ${e.getFullYear()}`;
};

// Color tag per motorowodne level for visual grouping
const motorowodneAccent = (mw: number): { bg: string; text: string; ring: string } => {
  if (mw === 1) return { bg: 'bg-sky-50', text: 'text-sky-700', ring: 'ring-sky-200' };
  if (mw === 2) return { bg: 'bg-indigo-50', text: 'text-indigo-700', ring: 'ring-indigo-200' };
  if (mw === 3) return { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' };
  return { bg: 'bg-amber-50', text: 'text-amber-700', ring: 'ring-amber-200' };
};

const UpcomingTermCard = ({ term, index }: { term: UpcomingTerm; index: number }) => {
  const accent = motorowodneAccent(term.motorowodne);
  const start = new Date(term.datarozpoczecia);
  const day = start.getDate();
  const month = PL_MONTHS_SHORT[start.getMonth()];
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: Math.min(index, 6) * 0.05 }}
    >
      <Link
        href={`/event/${term.eventId}`}
        className="group flex items-center gap-4 p-4 bg-white rounded-2xl border border-slate-200/70 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-900/5 transition-all"
      >
        {/* Date block */}
        <div className={`flex flex-col items-center justify-center w-16 h-16 rounded-xl ring-1 ${accent.bg} ${accent.text} ${accent.ring} shrink-0`}>
          <span className="text-2xl font-bold leading-none">{day}</span>
          <span className="text-[11px] uppercase tracking-wider mt-0.5">{month}</span>
        </div>
        {/* Body */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold ${accent.bg} ${accent.text}`}>
              {term.categoryShortName}
            </span>
            <span className="text-xs text-slate-500">{formatDateRange(term.datarozpoczecia, term.datazakonczenia)}</span>
          </div>
          <h4 className="font-semibold text-slate-800 text-sm leading-snug truncate">{term.courseTitle}</h4>
          {term.cena && (
            <p className="text-xs text-slate-500 mt-1 truncate">{term.cena}</p>
          )}
        </div>
        <FaArrowRight className="w-4 h-4 text-slate-300 group-hover:text-sky-600 group-hover:translate-x-1 transition-all shrink-0" />
      </Link>
    </motion.div>
  );
};

// Timeline Step Component with enhanced visuals
const TimelineStep = ({ step, index, isLast }: { step: any; index: number; isLast: boolean }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      className="relative flex gap-6 md:gap-10"
      initial={{ opacity: 0, x: -50 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ delay: index * 0.2, duration: 0.6 }}
    >
      <div className="flex flex-col items-center">
        <motion.div
          className={`relative w-16 h-16 rounded-2xl flex items-center justify-center text-white font-bold z-10 ${step.color} shadow-lg`}
          initial={{ scale: 0, rotate: -180 }}
          animate={isInView ? { scale: 1, rotate: 0 } : {}}
          transition={{ delay: index * 0.2 + 0.3, type: 'spring', stiffness: 200 }}
        >
          <div className="absolute inset-0 rounded-2xl bg-white/20 animate-pulse" />
          {step.icon}
        </motion.div>
        {!isLast && (
          <motion.div
            className="w-1 flex-1 mt-3"
            style={{
              background: 'linear-gradient(to bottom, rgb(6, 40, 95), rgb(56, 189, 248))',
            }}
            initial={{ scaleY: 0 }}
            animate={isInView ? { scaleY: 1 } : {}}
            transition={{ delay: index * 0.2 + 0.5, duration: 0.5 }}
          />
        )}
      </div>

      <div className="flex-1 pb-12">
        <motion.div
          className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/50 hover:shadow-2xl hover:bg-white transition-all duration-300"
          whileHover={{ scale: 1.02 }}
        >
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <h4 className="text-xl font-bold text-slate-800">{step.title}</h4>
            <span className="px-3 py-1 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-full text-sm font-medium shadow-sm">
              {step.duration}
            </span>
          </div>
          <p className="text-slate-600 mb-4">{step.description}</p>
          <ul className="space-y-2">
            {step.items.map((item: string, i: number) => (
              <motion.li
                key={i}
                className="flex items-center gap-3 text-sm text-slate-600"
                initial={{ opacity: 0, x: -10 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: index * 0.2 + 0.4 + i * 0.1 }}
              >
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-sky-500 to-blue-600" />
                {item}
              </motion.li>
            ))}
          </ul>
        </motion.div>
      </div>
    </motion.div>
  );
};

// Structured data for SEO
const courseSchema = {
  '@context': 'https://schema.org',
  '@type': 'Course',
  name: 'Kurs na Patent Motorowodny - Sternik Motorowodny',
  description: 'Kompleksowy kurs przygotowujący do egzaminu państwowego na patent sternika motorowodnego.',
  provider: {
    '@type': 'EducationalOrganization',
    name: 'Ośrodek Szkolenia Morskiego LIBRA',
    address: { '@type': 'PostalAddress', addressLocality: 'Kołobrzeg', addressCountry: 'PL' },
  },
  educationalCredentialAwarded: 'Patent Sternika Motorowodnego',
};

// Main Page Component
export default function MotorowodneClient() {
  const [courses, setCourses] = useState<MotorowodneCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [openFAQ, setOpenFAQ] = useState<number | null>(0);
  const [cmsContent, setCmsContent] = useState<CMSContent>({});
  const [cmsFlat, setCmsFlat] = useState<CMSFlat>({});
  const { scrollYProgress } = useScroll();
  const heroRef = useRef(null);

  // Parallax transforms
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);

  // Helper function to get CMS content with fallback
  const getCms = (group: string, key: string, field: 'title' | 'content' = 'title', fallback: string = ''): string => {
    return cmsContent[group]?.[key]?.[field] || fallback;
  };

  const getCmsExtra = (group: string, key: string): any => {
    return cmsContent[group]?.[key]?.extra_data || null;
  };

  // Fetch courses and CMS content from database
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch courses and CMS content in parallel
        const [coursesRes, cmsRes] = await Promise.all([
          fetch('/api/motorowodne'),
          fetch('/api/motorowodne-content')
        ]);

        const coursesData = await coursesRes.json();
        if (coursesData.success && coursesData.data?.courses) {
          setCourses(coursesData.data.courses);
        }

        const cmsData = await cmsRes.json();
        if (cmsData.success && cmsData.data) {
          setCmsContent(cmsData.data);
          if (cmsData.flat) {
            setCmsFlat(cmsData.flat);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Training timeline steps - from CMS or fallback
  const defaultTimelineSteps = [
    {
      title: 'Część Teoretyczna',
      duration: '8-12 godzin',
      description: 'Wykłady i materiały szkoleniowe przygotowujące do egzaminu teoretycznego.',
      color: 'bg-gradient-to-br from-sky-500 to-sky-600',
      icon: <FaGraduationCap className="w-7 h-7" />,
      items: ['Przepisy prawne i żeglugowe', 'Budowa jachtu motorowego', 'Obsługa silnika i mechanika', 'Nawigacja i znaki wodne', 'Meteorologia i bezpieczeństwo'],
    },
    {
      title: 'Zajęcia Praktyczne',
      duration: '4-6 godzin',
      description: 'Praktyczna nauka prowadzenia łodzi motorowej na wodzie z instruktorem.',
      color: 'bg-gradient-to-br from-blue-600 to-blue-700',
      icon: <GiSpeedBoat className="w-7 h-7" />,
      items: ['Manewrowanie i cumowanie', 'Odbijanie od nabrzeża', 'Kotwiczenie', 'Procedura "człowiek za burtą"', 'Holowanie jednostek'],
    },
    {
      title: 'Egzamin Państwowy',
      duration: '1 dzień',
      description: 'Oficjalny egzamin przed komisją egzaminacyjną PZMWiNW.',
      color: 'bg-gradient-to-br from-indigo-600 to-indigo-700',
      icon: <FaClipboardCheck className="w-7 h-7" />,
      items: ['Test teoretyczny (30 pytań)', 'Próg zdawalności 75%', 'Egzamin praktyczny na wodzie', 'Możliwość poprawy każdej części'],
    },
    {
      title: 'Otrzymanie Patentu',
      duration: 'Bezterminowo',
      description: 'Oficjalny dokument uprawniający do prowadzenia łodzi motorowych.',
      color: 'bg-gradient-to-br from-emerald-500 to-emerald-600',
      icon: <FaCertificate className="w-7 h-7" />,
      items: ['Patent wydawany przez PZMWiNW', 'Ważność bezterminowa', 'Uznawany w całej UE', 'Możliwość uzyskania duplikatu'],
    },
  ];

  // Build timeline from CMS if available
  const timelineSteps = cmsFlat.timeline_steps?.length > 0
    ? cmsFlat.timeline_steps.map((item, i) => ({
        title: item.title || defaultTimelineSteps[i]?.title || '',
        duration: item.extra_data?.duration || defaultTimelineSteps[i]?.duration || '',
        description: item.content || defaultTimelineSteps[i]?.description || '',
        color: item.extra_data?.color || defaultTimelineSteps[i]?.color || 'bg-gradient-to-br from-sky-500 to-sky-600',
        icon: ICON_MAP[item.extra_data?.icon] || defaultTimelineSteps[i]?.icon || <FaAnchor className="w-7 h-7" />,
        items: item.extra_data?.items || defaultTimelineSteps[i]?.items || [],
      }))
    : defaultTimelineSteps;

  // Benefits data - from CMS or fallback
  const defaultBenefits = [
    { icon: <FaWater className="w-7 h-7" />, title: 'Wolność na wodzie', desc: 'Prowadź łódź samodzielnie' },
    { icon: <GiSpeedBoat className="w-7 h-7" />, title: 'Wypożyczanie łodzi', desc: 'Legalny wynajem jednostek' },
    { icon: <FaLifeRing className="w-7 h-7" />, title: 'Bezpieczeństwo', desc: 'Znajomość procedur ratunkowych' },
    { icon: <FaCompass className="w-7 h-7" />, title: 'Nawigacja', desc: 'Umiejętność żeglugi' },
    { icon: <FaInfinity className="w-7 h-7" />, title: 'Bezterminowy', desc: 'Patent ważny na zawsze' },
    { icon: <FaGlobe className="w-7 h-7" />, title: 'Uznanie w UE', desc: 'Pływaj w całej Europie' },
    { icon: <FaHandshake className="w-7 h-7" />, title: 'Wartość zawodowa', desc: 'Dodatkowa kwalifikacja' },
    { icon: <HiSparkles className="w-7 h-7" />, title: 'Nowe możliwości', desc: 'Sport, turystyka, wędkarstwo' },
  ];

  const benefits = cmsFlat.benefits_items?.length > 0
    ? cmsFlat.benefits_items.map((item, i) => ({
        icon: ICON_MAP[item.extra_data?.icon] || defaultBenefits[i]?.icon || <FaAnchor className="w-7 h-7" />,
        title: item.title || defaultBenefits[i]?.title || '',
        desc: item.content || defaultBenefits[i]?.desc || '',
      }))
    : defaultBenefits;

  // FAQ data - from CMS or fallback (dane zgodne z PZMWiNW)
  // W Polsce istnieją TYLKO DWA patenty motorowodne: SM i MSM
  const defaultFaqData = [
    { q: 'Jakie patenty motorowodne istnieją w Polsce?', a: 'W Polsce istnieją dwa patenty motorowodne wydawane przez PZMWiNW: Sternik Motorowodny (SM) - podstawowy patent na wody śródlądowe i ograniczony dostęp do morza, oraz Morski Sternik Motorowodny (MSM) - patent uprawniający do pływania na pełnym morzu jednostkami do 18m.' },
    { q: 'Od ilu lat można zrobić patent motorowodny?', a: 'Patent sternika motorowodnego (SM) można uzyskać od ukończenia 14 roku życia. Osoby w wieku 14-16 lat mają ograniczenie do silników o mocy do 60 kW. Patent morskiego sternika motorowodnego (MSM) wymaga ukończenia 18 lat oraz minimum 200 godzin stażu pływania.' },
    { q: 'Ile kosztuje patent motorowodny?', a: 'Koszt uzyskania patentu składa się z: ceny kursu (aktualne ceny znajdziesz w naszym katalogu kursów powyżej), opłaty egzaminacyjnej (około 250 PLN) oraz opłaty za wydanie dokumentu (około 50 PLN). Patent morskiego sternika motorowodnego (MSM) wymaga dodatkowo udokumentowania 200h stażu pływania.' },
    { q: 'Czy kurs na patent motorowodny jest obowiązkowy?', a: 'Kurs nie jest prawnie obowiązkowy - można przystąpić do egzaminu jako ekstern. Jednak zdecydowanie zalecamy ukończenie kursu ze względu na praktyczne przygotowanie. Statystyki pokazują znacznie wyższą zdawalność wśród osób po kursie.' },
    { q: 'Ile trwa kurs na patent motorowodny?', a: 'Typowy kurs na patent SM trwa 2 dni weekendowe lub 3-4 wieczory. Obejmuje około 10-12 godzin teorii i 4-6 godzin praktyki na wodzie. Kursy intensywne mogą być realizowane nawet w jeden pełny weekend.' },
    { q: 'Jaką łodzią mogę prowadzić z patentem sternika motorowodnego (SM)?', a: 'Z patentem SM możesz prowadzić jachty motorowe po wodach śródlądowych (bez limitu długości), a na wodach morskich wewnętrznych jednostki do 12 metrów długości kadłuba. W pasie 2 Mm od brzegu możesz pływać tylko w porze dziennej. Możesz także prowadzić skutery wodne.' },
    { q: 'Czym różni się SM od MSM?', a: 'Sternik Motorowodny (SM) uprawnia do prowadzenia łodzi na wodach śródlądowych i do 12m na wodach morskich wewnętrznych oraz do 2 Mm od brzegu (tylko w porze dziennej). Morski Sternik Motorowodny (MSM) pozwala na prowadzenie jednostek do 18m długości kadłuba na pełnych wodach morskich bez ograniczenia odległości od brzegu. MSM wymaga 200 godzin stażu (minimum 2 rejsy morskie).' },
    { q: 'Czy patent motorowodny uprawnia do prowadzenia skutera wodnego?', a: 'Tak, patent sternika motorowodnego (SM) w pełni uprawnia do prowadzenia skuterów wodnych (jet ski). Nie potrzebujesz oddzielnych uprawnień na skuter wodny.' },
    { q: 'Jak wygląda egzamin na patent motorowodny?', a: 'Egzamin składa się z dwóch części. Teoretyczna to test wielokrotnego wyboru (30 pytań, 30 minut, próg 75%). Praktyczna to manewry na wodzie: odbijanie, cumowanie, manewry między bojami, procedura "człowiek za burtą". Można poprawiać każdą część osobno.' },
    { q: 'Czy muszę umieć pływać?', a: 'Tak, umiejętność pływania jest wymagana. Potwierdza się ją oświadczeniem własnym lub dokumentem (dyplom szkoły pływania). Podczas egzaminu nie ma testu pływania, ale jest to wymóg formalny.' },
    { q: 'Czy patent motorowodny wymaga odnawiania?', a: 'Nie, oba patenty motorowodne (SM i MSM) mają bezterminową ważność i nie wymagają odnawiania. Po zdaniu egzaminu patent jest ważny do końca życia. W przypadku utraty dokumentu można uzyskać duplikat.' },
    { q: 'Czy potrzebuję badań lekarskich?', a: 'Do patentu SM wystarczy oświadczenie o braku przeciwwskazań zdrowotnych. Zaświadczenie lekarskie nie jest obligatoryjne, ale zalecane. Patent MSM wymaga zaświadczenia lekarskiego.' },
    { q: 'Jak zdobyć staż wymagany do patentu MSM?', a: 'Do uzyskania patentu Morskiego Sternika Motorowodnego potrzebujesz 200 godzin stażu pływania, w tym minimum 2 rejsy morskie. Staż dokumentujesz w żeglarskiej książeczce pływań. Rejsy można odbywać jako członek załogi na różnych jednostkach motorowych.' },
    { q: 'Czy mogę holować narciarza wodnego z patentem sternika?', a: 'Do holowania narciarza wodnego potrzebujesz dodatkowej licencji na holowanie narciarza wodnego. Wymaga ona posiadania minimum patentu sternika motorowodnego (SM) i ukończenia 18 lat.' },
  ];

  const faqData = cmsFlat.faq_items?.length > 0
    ? cmsFlat.faq_items.map((item, i) => ({
        q: item.title || defaultFaqData[i]?.q || '',
        a: item.content || defaultFaqData[i]?.a || '',
      }))
    : defaultFaqData;

  // Group courses by motorowodne level for comparison table
  // W Polsce są TYLKO DWA patenty motorowodne: SM i MSM
  // 1 = Sternik Motorowodny (SM), 2 = Morski Sternik Motorowodny (MSM)
  const coursesByLevel = {
    sm: courses.filter(c => c.motorowodne === 1),
    msm: courses.filter(c => c.motorowodne === 2 || c.motorowodne === 3),
  };

  // Flatten upcoming events across all motorowodne courses, sorted by start date
  const upcomingTerms: UpcomingTerm[] = courses
    .flatMap((course) =>
      (course.upcomingEvents || []).map((ev: any) => ({
        eventId: ev.id,
        courseId: course.id,
        courseTitle: course.tytul,
        categoryShortName: course.categoryShortName || 'Motorowodne',
        motorowodne: course.motorowodne,
        datarozpoczecia: ev.datarozpoczecia,
        datazakonczenia: ev.datazakonczenia,
        cena: ev.cena ?? course.cena ?? null,
      }))
    )
    .sort((a, b) => a.datarozpoczecia.localeCompare(b.datarozpoczecia));

  // Get prices from courses - shows actual prices from database
  const getPricesFromCourses = (coursesArr: MotorowodneCourse[]): string => {
    if (coursesArr.length === 0) {
      return 'sprawdź w katalogu';
    }

    // Get unique prices from all courses
    const prices = coursesArr
      .map(c => c.cena)
      .filter((cena): cena is string => cena !== null && cena !== undefined && cena.trim() !== '');

    if (prices.length === 0) {
      return 'sprawdź w katalogu';
    }

    // If only one unique price, return it
    const uniquePrices = [...new Set(prices)];
    if (uniquePrices.length === 1) {
      return uniquePrices[0];
    }

    // If multiple prices, show range or list
    return uniquePrices.slice(0, 2).join(' / ');
  };

  // Comparison table data - zgodne z PZMWiNW (tylko SM i MSM)
  // Ceny pobierane dynamicznie z kursów w katalogu
  const defaultComparisonData = [
    { feature: 'Długość jednostki (morze)', sm: 'do 12 m', msm: 'do 18 m' },
    { feature: 'Wody śródlądowe', sm: 'bez limitu', msm: 'bez limitu' },
    { feature: 'Wody morskie wewnętrzne', sm: 'do 12 m', msm: true },
    { feature: 'Pas 2 Mm od brzegu', sm: 'pora dzienna', msm: true },
    { feature: 'Strefa pełnomorska', sm: false, msm: true },
    { feature: 'Wymagany wiek', sm: '14 lat*', msm: '18 lat' },
    { feature: 'Wymagany staż', sm: 'brak', msm: '200h (2 rejsy morskie)' },
    { feature: 'Skuter wodny', sm: true, msm: true },
    { feature: 'Ważność patentu', sm: 'bezterminowo', msm: 'bezterminowo' },
    {
      feature: 'Cena kursu',
      sm: getPricesFromCourses(coursesByLevel.sm),
      msm: getPricesFromCourses(coursesByLevel.msm),
    },
  ];

  // Get comparison data from CMS if available
  const comparisonCmsData = getCmsExtra('comparison', 'comparison_data');
  const comparisonData = comparisonCmsData?.rows || defaultComparisonData;

  // Generate FAQ schema
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map(item => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <>
      {/* JSON-LD Structured Data - using script with type for SEO */}
      <script
        type="application/ld+json"
        suppressHydrationWarning
        dangerouslySetInnerHTML={{ __html: JSON.stringify(courseSchema) }}
      />
      <script
        type="application/ld+json"
        suppressHydrationWarning
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <main className="relative overflow-hidden">
        {/* Hero Section — minimalist nautical aesthetic */}
        <section ref={heroRef} className="relative min-h-[68vh] md:min-h-[72vh] flex items-center justify-center overflow-hidden pt-20 pb-16">
          {/* Layered gradient background */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-[rgb(6,40,95)] via-[rgb(10,50,110)] to-[rgb(6,40,95)]" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(56,189,248,0.18),transparent_55%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(99,102,241,0.12),transparent_55%)]" />
            <div
              className="absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage:
                  'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
                backgroundSize: '64px 64px',
              }}
            />
          </div>

          <SubtleWave scrollProgress={scrollYProgress} position="bottom" color="rgba(255,255,255,0.22)" amplitude={30} frequency={1.2} />

          {/* Hero Content */}
          <motion.div
            className="relative z-10 text-center px-4 max-w-4xl mx-auto"
            style={{ y: y1, opacity }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-6"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-sky-200 text-xs md:text-sm font-medium border border-white/20 uppercase tracking-wider">
                <FaAnchor className="w-3.5 h-3.5" />
                {getCms('hero', 'hero_badge', 'title', 'Patenty PZMWiNW · Ośrodek LIBRA')}
              </span>
            </motion.div>

            <motion.h1
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-[1.05] tracking-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {getCms('hero', 'hero_title', 'title', 'Patent')}{' '}
              <span className="relative inline-block">
                <span className="relative z-10 bg-gradient-to-r from-sky-300 via-cyan-200 to-sky-300 bg-clip-text text-transparent">
                  {getCms('hero', 'hero_title_highlight', 'title', 'motorowodny')}
                </span>
                <motion.span
                  className="absolute -bottom-1 left-0 right-0 h-2.5 bg-gradient-to-r from-amber-400/60 to-orange-400/60 rounded-full blur-sm"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 0.9, duration: 0.7 }}
                />
              </span>
            </motion.h1>

            <motion.p
              className="text-lg md:text-xl text-sky-100/85 mb-10 max-w-2xl mx-auto leading-relaxed font-light"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              {getCms('hero', 'hero_subtitle', 'content', 'Sternik motorowodny i Morski sternik motorowodny — dwa oficjalne patenty PZMWiNW. Kursy weekendowe w Kołobrzegu, profesjonalna kadra, 98% zdawalność.')}
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-3 justify-center mb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45 }}
            >
              <Link
                href={getCms('hero', 'hero_cta_primary', 'content', '#terminy')}
                className="group px-7 py-3.5 bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500 text-slate-900 font-bold rounded-full hover:shadow-xl hover:shadow-amber-500/30 transition-all flex items-center justify-center gap-2.5 hover:scale-[1.02]"
              >
                <FaCalendarAlt className="w-4 h-4" />
                <span>{getCms('hero', 'hero_cta_primary', 'title', 'Zobacz najbliższe terminy')}</span>
                <FaArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                href={getCms('hero', 'hero_cta_secondary', 'content', '#patenty')}
                className="px-7 py-3.5 bg-white/10 backdrop-blur-md text-white font-semibold rounded-full border border-white/30 hover:bg-white/20 transition-all flex items-center justify-center gap-2"
              >
                <FaCompass className="w-4 h-4" />
                {getCms('hero', 'hero_cta_secondary', 'title', 'Porównaj patenty')}
              </Link>
            </motion.div>

            {/* Trust pills — concrete facts under CTA */}
            <motion.div
              className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sky-100/80 text-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.6 }}
            >
              <span className="inline-flex items-center gap-1.5"><FaCheck className="w-3 h-3 text-amber-300" /> Już od 14 lat (SM)</span>
              <span className="inline-flex items-center gap-1.5"><FaCheck className="w-3 h-3 text-amber-300" /> 2–3 dni weekendowe</span>
              <span className="inline-flex items-center gap-1.5"><FaCheck className="w-3 h-3 text-amber-300" /> Patent bezterminowy</span>
              <span className="inline-flex items-center gap-1.5"><FaCheck className="w-3 h-3 text-amber-300" /> Uznanie w UE</span>
            </motion.div>
          </motion.div>
        </section>

        {/* Sticky in-page navigation — anchors to main sections */}
        <StickyAnchorNav />

        {/* Stats Section with glass cards */}
        <section className="relative py-16 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
          <div className="max-w-6xl mx-auto px-4 relative z-10">
            <motion.div
              className="grid grid-cols-2 md:grid-cols-4 gap-5"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              {[
                { value: 15, suffix: '+', label: 'Lat doświadczenia', icon: <FaCertificate className="w-6 h-6" />, color: 'from-amber-400 to-orange-500' },
                { value: 98, suffix: '%', label: 'Zdawalność egzaminów', icon: <FaClipboardCheck className="w-6 h-6" />, color: 'from-emerald-400 to-teal-500' },
                { value: 2500, suffix: '+', label: 'Przeszkolonych kursantów', icon: <FaUserGraduate className="w-6 h-6" />, color: 'from-blue-400 to-indigo-500' },
                { value: 2, suffix: '', label: 'Patenty PZMWiNW (SM, MSM)', icon: <GiSpeedBoat className="w-6 h-6" />, color: 'from-purple-400 to-pink-500' },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  className="group relative"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500`} />
                  <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/50 text-center group-hover:shadow-2xl group-hover:-translate-y-1 transition-all duration-300">
                    <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${stat.color} text-white mb-4 group-hover:scale-110 transition-transform`}>
                      {stat.icon}
                    </div>
                    <div className="text-4xl font-bold text-slate-800 mb-1">
                      <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                    </div>
                    <div className="text-slate-500 text-sm font-medium">{stat.label}</div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="relative py-24 px-4 bg-white overflow-hidden">
          <div className="max-w-6xl mx-auto relative z-10">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-sky-100 to-blue-100 text-sky-700 rounded-full text-sm font-semibold mb-4">
                {getCms('intro', 'intro_badge', 'title', 'Czym jest patent motorowodny?')}
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                {getCms('intro', 'intro_title', 'title', 'Twój klucz do swobodnej żeglugi')}
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
                {getCms('intro', 'intro_description', 'content', 'Patent motorowodny to oficjalny dokument potwierdzający Twoje kwalifikacje do prowadzenia łodzi motorowych. Wydawany przez Polski Związek Motorowodny i Narciarstwa Wodnego, jest Twoją przepustką do niezależności na wodzie.')}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: <FaCertificate className="w-10 h-10" />,
                  title: 'Oficjalny dokument',
                  desc: 'Uznawany przez wszystkie służby żeglugi w Polsce i Unii Europejskiej.',
                  gradient: 'from-sky-500 to-blue-600',
                },
                {
                  icon: <FaInfinity className="w-10 h-10" />,
                  title: 'Bezterminowa ważność',
                  desc: 'Patent nie wymaga odnawiania - jest ważny przez całe życie.',
                  gradient: 'from-blue-500 to-indigo-600',
                },
                {
                  icon: <FaGlobe className="w-10 h-10" />,
                  title: 'Uznanie w UE',
                  desc: 'Możesz legalnie pływać po wodach wszystkich krajów Unii Europejskiej.',
                  gradient: 'from-indigo-500 to-purple-600',
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  className="group relative"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                >
                  <div className={`absolute -inset-1 bg-gradient-to-r ${item.gradient} rounded-3xl blur-xl opacity-0 group-hover:opacity-25 transition-opacity duration-500`} />
                  <div className="relative h-full bg-white rounded-3xl p-8 shadow-lg border border-slate-100 group-hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-2">
                    <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${item.gradient} text-white mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                      {item.icon}
                    </div>
                    <h3 className="text-2xl font-bold text-slate-800 mb-3">{item.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Courses from Database Section */}
        <section id="kursy" className="relative py-24 px-4 bg-gradient-to-b from-slate-50 via-sky-50/30 to-white overflow-hidden">
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-700 rounded-full text-sm font-semibold mb-4">
                {getCms('courses', 'courses_badge', 'title', 'Nasze kursy')}
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                {getCms('courses', 'courses_title', 'title', 'Szkolenia motorowodne')}
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                {getCms('courses', 'courses_description', 'content', 'Wybierz kurs dopasowany do Twoich potrzeb i zdobądź patent motorowodny.')}
              </p>
            </motion.div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="relative w-16 h-16">
                  <div className="absolute inset-0 border-4 border-sky-200 rounded-full" />
                  <div className="absolute inset-0 border-4 border-sky-600 border-t-transparent rounded-full animate-spin" />
                </div>
                <p className="mt-4 text-slate-500">Ładowanie kursów...</p>
              </div>
            ) : courses.length > 0 ? (
              <>
                <CoursesCarousel courses={courses} />
                <motion.div
                  className="text-center mt-12"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                >
                  <Link
                    href="/training"
                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[rgb(6,40,95)] to-[rgb(15,60,130)] text-white font-semibold rounded-full hover:shadow-xl hover:shadow-blue-900/20 transition-all hover:scale-105"
                  >
                    Zobacz pełny katalog szkoleń
                    <FaArrowRight className="w-4 h-4" />
                  </Link>
                </motion.div>
              </>
            ) : (
              <motion.div
                className="text-center py-16 bg-white rounded-3xl border border-slate-100 shadow-xl"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="inline-flex p-6 rounded-full bg-sky-100 text-sky-600 mb-6">
                  <GiSpeedBoat className="w-12 h-12" />
                </div>
                <h3 className="text-2xl font-bold text-slate-700 mb-3">
                  Kursy motorowodne wkrótce dostępne
                </h3>
                <p className="text-slate-500 mb-8 max-w-md mx-auto">
                  Przygotowujemy dla Ciebie ofertę szkoleń motorowodnych. Skontaktuj się z nami, aby poznać szczegóły.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    href="/training"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[rgb(6,40,95)] to-[rgb(15,60,130)] text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                  >
                    Zobacz wszystkie kursy
                    <FaArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    href="/contact"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-colors"
                  >
                    <FaPhone className="w-4 h-4" />
                    Skontaktuj się
                  </Link>
                </div>
              </motion.div>
            )}
          </div>
        </section>

        {/* Upcoming Terms Section — flat list of all motorowodne dates */}
        <section id="terminy" className="relative py-20 px-4 bg-white overflow-hidden">
          <div className="max-w-5xl mx-auto relative z-10">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 rounded-full text-sm font-semibold mb-4">
                Harmonogram
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Najbliższe terminy szkoleń
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Wybierz dogodny termin i przejdź do rezerwacji. Wszystkie szkolenia odbywają się w Kołobrzegu.
              </p>
            </motion.div>

            {loading ? (
              <div className="flex justify-center py-10">
                <div className="w-10 h-10 border-4 border-sky-200 border-t-sky-600 rounded-full animate-spin" />
              </div>
            ) : upcomingTerms.length > 0 ? (
              <>
                <div className="grid md:grid-cols-2 gap-3">
                  {upcomingTerms.slice(0, 8).map((term, i) => (
                    <UpcomingTermCard key={term.eventId} term={term} index={i} />
                  ))}
                </div>
                {upcomingTerms.length > 8 && (
                  <div className="text-center mt-8">
                    <Link
                      href="/calendar"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-slate-100 text-slate-700 font-semibold rounded-full hover:bg-slate-200 transition-colors"
                    >
                      Zobacz wszystkie {upcomingTerms.length} terminów
                      <FaArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 bg-slate-50 rounded-2xl border border-slate-200">
                <FaCalendarAlt className="w-10 h-10 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 mb-1 font-medium">Brak najbliższych terminów</p>
                <p className="text-slate-500 text-sm mb-6">Skontaktuj się z nami — chętnie pomożemy ustalić dogodny termin szkolenia.</p>
                <Link
                  href="/contact"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[rgb(6,40,95)] text-white rounded-full text-sm font-semibold hover:bg-[rgb(15,60,130)] transition-colors"
                >
                  <FaPhone className="w-3.5 h-3.5" />
                  Skontaktuj się
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* Comparison Table Section */}
        <section id="patenty" className="relative py-24 px-4 bg-white overflow-hidden">
          <div className="max-w-6xl mx-auto relative z-10">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 rounded-full text-sm font-semibold mb-4">
                Porównanie
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Rodzaje patentów motorowodnych
              </h2>
              <p className="text-slate-600">Szczegółowe zestawienie uprawnień i wymagań</p>
            </motion.div>

            <motion.div
              className="overflow-hidden rounded-3xl shadow-2xl border border-slate-100"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-[rgb(6,40,95)] to-[rgb(15,60,130)]">
                    <tr>
                      <th className="px-4 md:px-6 py-5 text-left text-white font-semibold text-sm">Cecha / Uprawnienie</th>
                      <th className="px-4 md:px-8 py-5 text-center text-white font-semibold">
                        <div className="flex flex-col items-center gap-2">
                          <IoMdBoat className="w-7 h-7" />
                          <span className="text-sm md:text-base font-bold">Sternik Motorowodny</span>
                          <span className="text-xs opacity-75">(SM)</span>
                        </div>
                      </th>
                      <th className="px-4 md:px-8 py-5 text-center text-white font-semibold">
                        <div className="flex flex-col items-center gap-2">
                          <FaShip className="w-7 h-7" />
                          <span className="text-sm md:text-base font-bold">Morski Sternik Motorowodny</span>
                          <span className="text-xs opacity-75">(MSM)</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonData.map((row: ComparisonRow, i: number) => (
                      <motion.tr
                        key={i}
                        className={`${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-sky-50/50 transition-colors`}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.05 }}
                      >
                        <td className="px-4 md:px-6 py-3 md:py-4 font-medium text-slate-700 text-sm">{row.feature}</td>
                        {['sm', 'msm'].map((key) => {
                          const value = row[key as keyof typeof row];
                          return (
                            <td key={key} className="px-4 md:px-8 py-3 md:py-4 text-center">
                              {typeof value === 'boolean' ? (
                                value ? (
                                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100">
                                    <FaCheck className="w-4 h-4 text-emerald-600" />
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-50">
                                    <FaTimes className="w-4 h-4 text-red-400" />
                                  </span>
                                )
                              ) : (
                                <span className="text-slate-600 font-medium text-sm">{value}</span>
                              )}
                            </td>
                          );
                        })}
                      </motion.tr>
                    ))}
                    {/* Row with links to courses */}
                    <motion.tr
                      className="bg-gradient-to-r from-sky-50 to-blue-50 border-t-2 border-sky-200"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: comparisonData.length * 0.05 }}
                    >
                      <td className="px-4 md:px-6 py-5 font-semibold text-slate-700 text-sm">Nasze kursy</td>
                      {[
                        { key: 'sm', courses: coursesByLevel.sm },
                        { key: 'msm', courses: coursesByLevel.msm },
                      ].map(({ key, courses: levelCourses }) => (
                        <td key={key} className="px-4 md:px-8 py-5 text-center">
                          {levelCourses.length > 0 ? (
                            <div className="flex flex-col gap-2 items-center">
                              {levelCourses.slice(0, 2).map((course) => (
                                <Link
                                  key={course.id}
                                  href={`/training/${courseSlug(course.id, course.tytul)}`}
                                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[rgb(6,40,95)] text-white text-sm font-medium rounded-lg hover:bg-[rgb(15,60,130)] transition-colors w-full max-w-[200px]"
                                >
                                  <span className="truncate">{course.tytul}</span>
                                  <FaArrowRight className="w-3 h-3 flex-shrink-0" />
                                </Link>
                              ))}
                              {levelCourses.length > 2 && (
                                <span className="text-xs text-slate-500">+{levelCourses.length - 2} więcej</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">Wkrótce dostępne</span>
                          )}
                        </td>
                      ))}
                    </motion.tr>
                  </tbody>
                </table>
              </div>
            </motion.div>

            {/* Note about age restriction */}
            <motion.p
              className="text-center text-sm text-slate-500 mt-6"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              * Osoby w wieku 14-16 lat mogą prowadzić jednostki z silnikiem o mocy do 60 kW
            </motion.p>
          </div>
        </section>

        {/* Training Timeline Section */}
        <section id="przebieg" className="py-24 px-4 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-sky-100 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-100 rounded-full blur-3xl opacity-50 translate-y-1/2 -translate-x-1/2" />

          <div className="max-w-4xl mx-auto relative">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-sky-100 to-blue-100 text-sky-700 rounded-full text-sm font-semibold mb-4">
                {getCms('timeline', 'timeline_badge', 'title', 'Przebieg szkolenia')}
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                {getCms('timeline', 'timeline_title', 'title', 'Droga do patentu')}
              </h2>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                {getCms('timeline', 'timeline_description', 'content', 'Od pierwszego wykładu do oficjalnego dokumentu - poznaj etapy szkolenia motorowodnego.')}
              </p>
            </motion.div>

            <div className="relative">
              {timelineSteps.map((step, i) => (
                <TimelineStep key={i} step={step} index={i} isLast={i === timelineSteps.length - 1} />
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="relative py-24 px-4 bg-white overflow-hidden">
          <div className="max-w-6xl mx-auto relative z-10">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 rounded-full text-sm font-semibold mb-4">
                {getCms('benefits', 'benefits_badge', 'title', 'Korzyści')}
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                {getCms('benefits', 'benefits_title', 'title', 'Dlaczego warto mieć patent?')}
              </h2>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {benefits.map((benefit, i) => (
                <motion.div
                  key={i}
                  className="group relative"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-sky-400 to-blue-500 rounded-2xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500" />
                  <div className="relative h-full p-6 bg-white rounded-2xl shadow-lg border border-slate-100 group-hover:shadow-2xl group-hover:-translate-y-2 transition-all duration-300">
                    <div className="text-sky-600 mb-4 group-hover:scale-110 group-hover:text-sky-500 transition-all">
                      {benefit.icon}
                    </div>
                    <h3 className="font-bold text-slate-800 mb-2">{benefit.title}</h3>
                    <p className="text-sm text-slate-500">{benefit.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Band — final conversion block before FAQ */}
        <section className="relative py-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[rgb(6,40,95)] via-[rgb(10,50,110)] to-[rgb(13,60,130)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(56,189,248,0.18),transparent_55%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(245,158,11,0.12),transparent_55%)]" />
          <div className="relative max-w-5xl mx-auto z-10">
            <motion.div
              className="grid md:grid-cols-[1fr_auto] items-center gap-8"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div>
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-amber-400/15 text-amber-200 rounded-full text-xs font-semibold uppercase tracking-wider mb-4 border border-amber-300/20">
                  <HiSparkles className="w-3.5 h-3.5" />
                  Gotowy do startu?
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {upcomingTerms.length > 0
                    ? <>Najbliższe szkolenie już <span className="text-amber-300">{formatDateRange(upcomingTerms[0].datarozpoczecia, upcomingTerms[0].datazakonczenia)}</span></>
                    : <>Zarezerwuj termin szkolenia motorowodnego</>
                  }
                </h2>
                <p className="text-sky-100/85 text-base md:text-lg max-w-2xl">
                  Wybierz dogodny termin online lub zadzwoń — odpowiemy na wszystkie pytania i pomożemy w wyborze odpowiedniego patentu.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row md:flex-col gap-3 shrink-0">
                <Link
                  href="#terminy"
                  className="px-7 py-3.5 bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 font-bold rounded-full hover:shadow-xl hover:shadow-amber-500/30 transition-all flex items-center justify-center gap-2 hover:scale-[1.02] whitespace-nowrap"
                >
                  <FaCalendarAlt className="w-4 h-4" />
                  Wybierz termin
                </Link>
                <Link
                  href="/contact"
                  className="px-7 py-3.5 bg-white/10 backdrop-blur-md text-white font-semibold rounded-full border border-white/30 hover:bg-white/20 transition-all flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <FaPhone className="w-4 h-4" />
                  Zadzwoń
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="relative py-24 px-4 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
          <div className="max-w-4xl mx-auto relative z-10">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 rounded-full text-sm font-semibold mb-4">
                {getCms('faq', 'faq_badge', 'title', 'FAQ')}
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                {getCms('faq', 'faq_title', 'title', 'Najczęściej zadawane pytania')}
              </h2>
            </motion.div>

            <div className="space-y-3">
              {faqData.map((item, i) => (
                <FAQItem
                  key={i}
                  question={item.q}
                  answer={item.a}
                  isOpen={openFAQ === i}
                  onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                  index={i}
                />
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Custom styles */}
      <style jsx global>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&display=swap');
      `}</style>
    </>
  );
}
